import React, { useCallback } from "react";

import { toast } from "react-toastify";
import { api } from "../../utils/api";
import { getDeleteFileCustomErrorMessage } from "../../utils/errorMessages";
import type z from "zod";
import { schemas } from "../../api/generated";
import { buildUserCountText } from "../../utils/buildUserCountText";
import { ConfirmationModal } from "./ConfirmationModal";

type FileDTO = z.infer<typeof schemas.FileWithUsage>;
const ProblemDetails = schemas.ProblemDetails;

export function DeletionModal({
  showDeletionModal,
  setShowDeletionModal,
  token,
  selectedFile,
  onDeleted,
}: {
  showDeletionModal: boolean;
  setShowDeletionModal: React.Dispatch<React.SetStateAction<boolean>>;
  token: string;
  selectedFile: FileDTO | undefined;
  // eslint-disable-next-line no-unused-vars
  onDeleted: (file: FileDTO) => Promise<void>;
}) {
  const handleDeleteFile = useCallback(async () => {
    if (!selectedFile?.id) {
      console.warn("No file was selected for deletion.");
      return;
    }

    try {
      await api.deleteFile(token, selectedFile.id);

      onDeleted(selectedFile); // Fire and forget

      toast.success("File deleted successfully.");
    } catch (error) {
      const message = await ProblemDetails.parseAsync(error)
        .then(getDeleteFileCustomErrorMessage)
        .catch(() => "An error occurred while deleting the file.");
      toast.error(message);
    } finally {
      setShowDeletionModal(false);
    }
  }, [token, selectedFile, onDeleted, setShowDeletionModal]);

  return (
    <ConfirmationModal
      show={showDeletionModal}
      onShow={(show) => setShowDeletionModal(show)}
      prompt="Are you sure want to delete this file?"
      warning={
        selectedFile?.assignmentCount
          ? `This file is currently assigned to ${buildUserCountText({ count: selectedFile.assignmentCount })}. Deleting it will permanently remove the file and unassign it from all users!`
          : ""
      }
      confirmButtonText="Delete"
      onConfirmed={handleDeleteFile}
    >
      <div className="mb-4">
        <code>
          <mark>{selectedFile?.fileName}</mark>
        </code>
      </div>
    </ConfirmationModal>
  );
}
